import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TeamManagerClub {
  clubId: string;
  clubName: string;
  teamId: string | null;
  teamName: string | null;
}

interface ClubRelation {
  club_name: string | null;
}

function clubNameFrom(relation: ClubRelation | ClubRelation[] | null | undefined): string {
  if (!relation) return "";
  const club = Array.isArray(relation) ? relation[0] : relation;
  return club?.club_name?.trim() || "";
}

/**
 * Resolve the club (and team, when present) for a team manager.
 *
 * staff_assignments.user_id + role `team_manager` is the assignment used by
 * the rest of the app. `teams.team_owner` is the fallback when a manager
 * owns a team that is linked to a club but has no staff row yet.
 */
export async function fetchTeamManagerClub(userId: string): Promise<TeamManagerClub | null> {
  const { data: assignment, error: assignmentError } = await supabase
    .from("staff_assignments")
    .select("club_id, clubs(club_name)")
    .eq("user_id", userId)
    .eq("role", "team_manager")
    .or("status.eq.active,status.is.null")
    .limit(1)
    .maybeSingle();

  if (assignmentError) throw assignmentError;

  if (assignment?.club_id) {
    const { data: team } = await supabase
      .from("teams")
      .select("id, team_name")
      .eq("club_id", assignment.club_id)
      .eq("team_owner", userId)
      .eq("is_deleted", false)
      .limit(1)
      .maybeSingle();

    return {
      clubId: assignment.club_id,
      clubName: clubNameFrom(assignment.clubs as ClubRelation | ClubRelation[] | null),
      teamId: team?.id ?? null,
      teamName: team?.team_name ?? null,
    };
  }

  const { data: ownedTeam, error: teamError } = await supabase
    .from("teams")
    .select("id, team_name, club_id, clubs(club_name)")
    .eq("team_owner", userId)
    .eq("is_deleted", false)
    .not("club_id", "is", null)
    .limit(1)
    .maybeSingle();

  if (teamError) throw teamError;
  if (!ownedTeam?.club_id) return null;

  return {
    clubId: ownedTeam.club_id,
    clubName: clubNameFrom(ownedTeam.clubs as ClubRelation | ClubRelation[] | null),
    teamId: ownedTeam.id,
    teamName: ownedTeam.team_name,
  };
}

export function useTeamManagerClub(userId?: string) {
  const query = useQuery({
    queryKey: ["team-manager-club", userId],
    queryFn: () => fetchTeamManagerClub(userId as string),
    enabled: !!userId,
    retry: 1,
    staleTime: 60_000,
  });

  // A disabled query stays "pending" in TanStack Query. Treat a missing user
  // as settled so pages can leave "Loading..." and show the unassigned state.
  return {
    ...query,
    isPending: !!userId && query.isPending,
  };
}
